/**
 * Billing. On Mojo Up AI Cloud: the team's plan and its standing (from the entitlement Mojo Up
 * billing signs), then four tabs: Usage (every plan quota as a meter, from
 * `/cloud/v1/organisations/:id/quotas`), Invoices (kept by Stripe for Mojo Up billing, opened through
 * Manage billing), Billing details (the billing contact, owners, plan start, renewal and seats) and
 * Add-ons. Owners change the plan; admins read it; members see the usage only. A team without Teams sees
 * the way to Teams first and no meters (it is read-only until it moves), and a person's own space is an
 * account, never billed: it shows the price and the way to Teams (components/cloud/UpgradeToTeams.tsx).
 *
 * On a self-hosted Enterprise AIOE there is no nav entry: the licence comes from the Mojo Up customer
 * portal, so the page says so and points at the licence's standing and Updates.
 */

import { CLOUD_TRIAL_DAYS, quotaAmountText, type BillingPortalFlow, type CloudBilling, type CloudBillingDetails, type CloudOrganisationQuotas, type CloudQuotaRow, type LicenceView } from '@aioe/shared';
import { dateLong, dateMedium } from '@aioe/shared/wording/dateTime';
import { ChartPie, CirclePlus, CreditCard, Receipt } from '@gravity-ui/icons';
import { Button, Dropdown, Label } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { useCloud } from '../auth/AuthProvider.tsx';
import { BillingBanner, billingStatus, RedeemModal, SeatsModal, TrialModal } from '../components/cloud/Billing.tsx';
import { UpgradeToTeamsCard } from '../components/cloud/UpgradeToTeams.tsx';
import { stateMark as licenceMark } from '../components/LicencePanel.tsx';
import { Ring, StoragePanel, type Fill } from '../components/storage.tsx';
import { ArtEmpty, ErrorNote, ListBlock, ListGroup, ListRow, Page, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.billing!;
const SALES = 'sales@mojoup.com.au';
const longDay = (iso: string) => dateLong(iso);
const shortDay = (iso: string) => dateMedium(iso, { year: false });
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;
const LIVE = new Set(['trialing', 'active', 'past_due']);
const NEEDS_CARD = new Set(['paused', 'unpaid', 'incomplete']);

/** Opens an email to Mojo Up sales, about this team. */
const contactSales = (team: string) => window.location.assign(`mailto:${SALES}?subject=${encodeURIComponent(`Mojo Up AI Cloud: ${team}`)}`);

type Tab = 'usage' | 'invoices' | 'details' | 'add-ons';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'usage', label: 'Usage' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'details', label: 'Billing details' },
  { id: 'add-ons', label: 'Add-ons' },
];

export function BillingPage() {
  const cloud = useCloud();
  if (!isCloud() || !cloud) return <EnterpriseBilling />;
  // A person's own space is an account, never billed: the way to Teams, and nothing to meter.
  if (cloud.organisation.kind === 'personal') return <PersonalBilling />;
  return <CloudBillingView key={cloud.organisation.id} />;
}

/** Billing in a personal space: the account is free, and Teams is a team's. */
function PersonalBilling() {
  const cloud = useCloud()!;
  const teams = cloud.me.organisations.filter((o) => o.kind === 'team');
  const onTeams = teams.filter((o) => o.plan === 'teams').length;
  return (
    <Page>
      <PageHeader tone={tone} icon={<CreditCard />} title="Billing" subtitle="Your account is free. Teams is billed per person, to the team." />
      <div className="art-stats">
        <Stat tone={tone} icon={<CreditCard />} label="Your account" value="Free" detail="Sign-in, updates and downloads" />
        <Stat tone={tone} icon={<ChartPie />} label="Your teams" value={teams.length} detail={teams.length ? `${plural(onTeams, 'on Teams', 'on Teams')}` : 'None yet'} />
      </div>
      <UpgradeToTeamsCard tone={tone} />
    </Page>
  );
}

function CloudBillingView() {
  const cloud = useCloud()!;
  const navigate = useNavigate();
  const org = cloud.organisation;
  const team = org.kind === 'team';
  // The API shows billing to a team's owners and admins; owners change it.
  const reader = team && (org.role === 'owner' || org.role === 'admin');
  const owner = team && org.role === 'owner';
  const quotas = useLoad(() => routes.organisationQuotas(org.id), [org.id]);
  const billing = useLoad(() => (reader ? routes.billing(org.id) : Promise.resolve(undefined)), [org.id, reader]);
  const details = useLoad(() => (reader ? routes.billingDetails(org.id) : Promise.resolve(undefined)), [org.id, reader]);
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  // `/billing?organisation=<id>` (the workbench's link to a team's billing): switch to that team, then drop the parameter.
  const wanted = params.get('organisation');
  useEffect(() => {
    if (!wanted) return;
    if (wanted !== org.id && cloud.me.organisations.some((o) => o.id === wanted)) cloud.switchTo(wanted);
    const next = new URLSearchParams(params);
    next.delete('organisation');
    setParams(next, { replace: true });
  }, [wanted, org.id, cloud, params, setParams]);
  const [buying, setBuying] = useState<'trial' | 'seats' | 'redeem'>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const b = billing.data;
  const q = quotas.data;
  // A team without Teams (and not waiting on a payment, which Stripe settles): the way to Teams, and no meters.
  const free = b ? b.plan !== 'teams' && !NEEDS_CARD.has(b.status) : org.plan !== 'teams';
  const tabs = free ? TABS.filter((t) => (t.id === 'invoices' || t.id === 'details') && b?.hasBillingAccount) : TABS;
  /** Stripe's customer portal through Mojo Up billing: the card, the tax details, invoices, cancelling. */
  const manage = async (flow?: BillingPortalFlow) => {
    setBusy(true);
    setError(undefined);
    try {
      window.location.assign((await routes.billingPortal(org.id, flow)).url);
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  const afterSeats = () => {
    billing.reload();
    quotas.reload();
  };

  const tab: Tab | undefined = reader ? (tabs.find((t) => t.id === asked)?.id ?? (free ? tabs[0]?.id : 'usage')) : free ? undefined : 'usage';
  const used = b ? b.members + b.openInvitations : undefined;
  const full = (q?.rows ?? []).filter((r) => r.atLimit && r.limit !== 0);
  const next = b ? nextDate(b) : undefined;

  const modals = (
    <>
      {b && buying === 'trial' ? <TrialModal isOpen onClose={() => setBuying(undefined)} organisationId={org.id} billing={b} /> : null}
      {b && buying === 'seats' ? <SeatsModal isOpen onClose={() => setBuying(undefined)} organisationId={org.id} billing={b} onChanged={afterSeats} /> : null}
      {b && buying === 'redeem' ? <RedeemModal isOpen onClose={() => setBuying(undefined)} organisationId={org.id} onRedeemed={afterSeats} /> : null}
    </>
  );
  const tabBar =
    reader && tab && tabs.length > 1 ? (
      <div className="-m-1 max-w-full overflow-x-auto p-1">
        <Segment aria-label="Billing" size="sm" selectedKey={tab} onSelectionChange={(k) => setParams(k === tabs[0]?.id ? {} : { tab: String(k) })} className="w-fit min-w-max">
          {tabs.map((t) => (
            <Segment.Item key={t.id} id={t.id}>
              {t.label}
            </Segment.Item>
          ))}
        </Segment>
      </div>
    ) : null;
  const paperwork = (
    <>
      <PartBoundary key="invoices" name="Invoices" tone={tone}>
        {tab === 'invoices' && b ? <InvoicesTab billing={b} details={details.data} owner={owner} busy={busy} onOpen={() => void manage()} onTrial={() => setBuying('trial')} /> : null}
      </PartBoundary>
      <PartBoundary key="details" name="Billing details" tone={tone}>
        {tab === 'details' && b ? <DetailsTab billing={b} details={details.data} owner={owner} busy={busy} onManage={() => void manage()} /> : null}
      </PartBoundary>
    </>
  );

  if (free) {
    // A free team: what it is, the way to Teams first, and its paperwork if it ever had any. Nothing to meter.
    const more =
      owner && b && (b.canRedeem || b.hasBillingAccount) ? (
        <Dropdown>
          <Button size="sm" variant="secondary" aria-label="More for the plan" isDisabled={busy}>
            More
          </Button>
          <Dropdown.Popover placement="bottom end" className="min-w-48">
            <Dropdown.Menu aria-label="More for the plan" onAction={(key) => (key === 'redeem' ? setBuying('redeem') : void manage())}>
              {b.canRedeem ? (
                <Dropdown.Item id="redeem" textValue="Redeem a licence">
                  <Label>Redeem a licence</Label>
                </Dropdown.Item>
              ) : null}
              {b.hasBillingAccount ? (
                <Dropdown.Item id="manage" textValue="Manage billing">
                  <Label>Manage billing</Label>
                </Dropdown.Item>
              ) : null}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      ) : null;
    return (
      <Page>
        <PageHeader tone={tone} icon={<CreditCard />} title="Billing" subtitle={`${org.name} is on the free plan. Its work is kept safe, read-only, until you start Teams.`} />
        <PartBoundary key="summary" name="Plan summary" tone={tone}>
          <div className="art-stats">
            <Stat tone={tone} icon={<CreditCard />} label="Plan" value={b ? planName(b) : 'Free'} detail={b && b.status !== 'none' ? billingStatus(b).label : 'Read-only'} />
            <Stat tone={tone} icon={<CreditCard />} label="People" value={b?.members} detail={b ? (b.openInvitations ? plural(b.openInvitations, 'open invitation') : 'No open invitations') : 'Owners and admins see it'} />
            <Stat tone={tone} icon={<Receipt />} label="Free trial" value={b ? (b.canStartTrial ? `${CLOUD_TRIAL_DAYS} days` : 'Used') : undefined} detail={b ? (b.canStartTrial ? 'No card needed' : 'Already used') : ''} />
          </div>
          {b ? <BillingBanner billing={b} owner={owner} onManage={() => void manage(b.status === 'paused' || b.status === 'unpaid' ? 'payment_method_update' : undefined)} /> : null}
          <ErrorNote error={error ?? billing.error ?? details.error} />
          <UpgradeToTeamsCard
            tone={tone}
            {...(b ? { billing: b } : {})}
            actions={owner ? more : undefined}
          />
        </PartBoundary>
        {tabBar}
        {paperwork}
        {modals}
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        tone={tone}
        icon={<CreditCard />}
        title="Billing"
        subtitle={reader ? `${org.name}'s plan, what the team uses of it, its invoices and what can be added.` : `${org.name}'s plan and what the team uses of it. Its owners and admins see the billing.`}
      />
      <PartBoundary key="summary" name="Plan summary" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<CreditCard />} label="Plan" value={b ? planName(b) : q?.planLabel} detail={b ? (b.status === 'none' ? 'No subscription' : billingStatus(b).label) : 'The owners manage it'} />
          <Stat
            tone={tone}
            icon={<CreditCard />}
            label="Seats"
            value={b && used !== undefined ? (b.configured ? `${used} of ${b.seatLimit}` : used) : undefined}
            detail={b ? (b.configured ? (b.plan === 'teams' ? `${plural(b.seatsPurchased, 'seat')} bought` : 'Free: the owner only') : 'No seat limit') : ''}
            needsYou={!!b && b.configured && used !== undefined && b.plan === 'teams' && used >= b.seatLimit}
          />
          {reader ? <Stat tone={tone} icon={<Receipt />} label={next?.label ?? 'Renews'} value={next?.value} detail={next?.detail ?? ''} /> : null}
          <Stat tone={tone} icon={<ChartPie />} label="At the limit" value={q ? full.length : undefined} detail={q ? (full.length ? full.map((r) => r.label).join(', ') : 'Nothing is full') : ''} needsYou={full.length > 0} onPress={reader && tab !== 'usage' ? () => setParams({}) : undefined} />
        </div>
        {b ? <BillingBanner billing={b} owner={owner} onManage={() => void manage(b.status === 'paused' || b.status === 'unpaid' ? 'payment_method_update' : undefined)} /> : null}
        <ErrorNote error={error ?? quotas.error ?? billing.error ?? details.error} />
        <PlanCard
          organisation={org.name}
          reader={reader}
          owner={owner}
          billing={b}
          planLabel={q?.planLabel}
          busy={busy}
          onTrial={() => setBuying('trial')}
          onSeats={() => setBuying('seats')}
          onRedeem={() => setBuying('redeem')}
          onManage={(flow) => void manage(flow)}
          onHelp={() => navigate('/help')}
        />
      </PartBoundary>
      {tabBar}
      <PartBoundary key="usage" name="Usage" tone={tone}>
        {tab === 'usage' ? <UsageTab quotas={q} /> : null}
        {tab === 'usage' && reader ? <StoragePanel tone={tone} /> : null}
      </PartBoundary>
      {paperwork}
      <PartBoundary key="add-ons" name="Add-ons" tone={tone}>
        {tab === 'add-ons' && b ? <AddOnsTab organisation={org.name} billing={b} owner={owner} onSeats={() => setBuying('seats')} onTrial={() => setBuying('trial')} /> : null}
      </PartBoundary>
      {modals}
    </Page>
  );
}

/** "Free", "Teams", or "Trial until 29 October 2026". */
function planName(b: CloudBilling): string {
  if (b.status === 'trialing' && b.trialEnd) return `Trial until ${shortDay(b.trialEnd)}`;
  return b.plan === 'teams' ? 'Teams' : 'Free';
}

/** The team runs on a licence Mojo Up issued, not a subscription. */
const licensed = (b: CloudBilling): boolean => b.source === 'licence' && b.plan === 'teams';

/** The next date that matters: the trial's end, the renewal, or the day Teams ends. */
function nextDate(b: CloudBilling): { label: string; value: string; detail: string } | undefined {
  if (licensed(b) && b.licence) return { label: 'Licence ends', value: shortDay(b.licence.until), detail: 'Ask support to extend it' };
  if (b.status === 'trialing' && b.trialEnd) return { label: 'Trial ends', value: shortDay(b.trialEnd), detail: b.trialDaysLeft !== undefined ? `${plural(b.trialDaysLeft, 'day')} left` : '' };
  if (LIVE.has(b.status) && b.periodEnd) return b.cancelAtPeriodEnd ? { label: 'Teams ends', value: shortDay(b.periodEnd), detail: 'Then the free plan' } : { label: 'Renews', value: shortDay(b.periodEnd), detail: 'Monthly' };
  return { label: 'Renews', value: '—', detail: b.plan === 'free' ? 'Nothing to renew on Free' : '' };
}

/** The plan and its standing in words, from the entitlement. */
function standing(b: CloudBilling): string {
  const used = b.members + b.openInvitations;
  const seats = `${plural(b.seatsPurchased, 'seat')}, ${used} in use`;
  if (!b.configured) return 'Billing is not set up on this deployment, so teams are not limited by seats.';
  if (licensed(b) && b.licence) return `Licensed with ${seats}, until ${longDay(b.licence.until)}. Nothing is charged.${b.licence.note ? ` ${b.licence.note}` : ''} To change the seats or extend the licence, ask support.`;
  if (b.source === 'licence' && b.licence?.ended) return `The team is on the free plan since its licence ended${b.status === 'canceled' ? '' : ` on ${longDay(b.licence.until)}`}. Everyone keeps their access and nothing has been deleted; nobody new can join until there is a seat.`;
  switch (b.status) {
    case 'trialing':
      return `Free trial of Teams with ${seats}. Nothing is charged${b.trialEnd ? ` until ${longDay(b.trialEnd)}` : ''}; after that, ${b.priceText}${b.cancelAtPeriodEnd ? ', but the trial is set to end without carrying on' : ''}.`;
    case 'active':
      return b.cancelAtPeriodEnd
        ? `Teams with ${seats}. It ends${b.periodEnd ? ` on ${longDay(b.periodEnd)}` : ' at the end of this month'} and the team goes back to the free plan; nothing is deleted.`
        : `Teams with ${seats}.${b.periodEnd ? ` Renews on ${longDay(b.periodEnd)}` : ' Renews monthly'}, at ${b.priceText}.`;
    case 'past_due':
      return `Teams with ${seats}. The team keeps Teams while Stripe tries the payment again.`;
    case 'none':
      return b.canStartTrial ? `The free plan holds only the team's owner. A ${CLOUD_TRIAL_DAYS}-day trial of Teams, with no card, lets you invite people.` : "The free plan holds only the team's owner. Switch to Teams to invite people.";
    default:
      return `The team is on the free plan${b.status === 'canceled' ? ' since its Teams subscription ended' : ''}. Everyone keeps their access and nothing has been deleted; nobody new can join until there is a seat.`;
  }
}

function PlanCard({
  organisation,
  reader,
  owner,
  billing: b,
  planLabel,
  busy,
  onTrial,
  onSeats,
  onRedeem,
  onManage,
  onHelp,
}: {
  organisation: string;
  reader: boolean;
  owner: boolean;
  billing?: CloudBilling;
  planLabel?: string;
  busy: boolean;
  onTrial: () => void;
  onSeats: () => void;
  onRedeem: () => void;
  onManage: (flow?: BillingPortalFlow) => void;
  onHelp: () => void;
}) {
  const sales = (
    <Button size="sm" variant="secondary" onPress={() => contactSales(organisation)}>
      Contact sales
    </Button>
  );
  if (!reader || !b) {
    const text = reader ? 'Loading the plan…' : "The team's owners manage its plan, seats and invoices.";
    return (
      <ListGroup title="Plan">
        <ListRow icon={<CreditCard />} title={planLabel ?? '—'} description={text} />
      </ListGroup>
    );
  }
  const live = LIVE.has(b.status);
  const needsCard = NEEDS_CARD.has(b.status);
  const status = billingStatus(b);
  const over = b.members - b.seatLimit;
  // One primary action, then Contact sales, then at most a menu: three controls at most.
  let actions: ReactNode = sales;
  if (owner && b.configured && licensed(b)) {
    // A licensed team changes its seats or dates by asking Mojo Up: the support page, not Stripe.
    const ask = (
      <Button size="sm" variant="primary" onPress={onHelp}>
        Ask support
      </Button>
    );
    actions = b.hasBillingAccount ? (
      <>
        {ask}
        <Button size="sm" variant="secondary" onPress={() => onManage()} isDisabled={busy}>
          Manage billing
        </Button>
      </>
    ) : (
      ask
    );
  } else if (owner && b.configured) {
    actions = live ? (
      <>
        <Button size="sm" variant="primary" onPress={onSeats} isDisabled={busy}>
          Manage seats
        </Button>
        {sales}
        <Dropdown>
          <Button size="sm" variant="secondary" aria-label="More for the plan" isDisabled={busy}>
            More
          </Button>
          <Dropdown.Popover placement="bottom end" className="min-w-48">
            <Dropdown.Menu aria-label="More for the plan" onAction={(key) => onManage(key === 'cancel' ? 'subscription_cancel' : undefined)}>
              <Dropdown.Item id="manage" textValue="Manage billing">
                <Label>Manage billing</Label>
              </Dropdown.Item>
              {b.cancelAtPeriodEnd ? null : (
                <Dropdown.Item id="cancel" textValue="Cancel Teams" variant="danger">
                  <Label>Cancel Teams</Label>
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </>
    ) : needsCard ? (
      <>
        <Button size="sm" variant="primary" onPress={() => onManage('payment_method_update')} isDisabled={busy}>
          {b.status === 'incomplete' ? 'Finish payment' : 'Add a payment method'}
        </Button>
        {sales}
      </>
    ) : (
      <>
        <Button size="sm" variant="primary" onPress={onTrial} isDisabled={busy}>
          {b.canStartTrial ? 'Start free trial' : 'Switch to Teams'}
        </Button>
        {sales}
        <Dropdown>
          <Button size="sm" variant="secondary" aria-label="More for the plan" isDisabled={busy}>
            More
          </Button>
          <Dropdown.Popover placement="bottom end" className="min-w-48">
            <Dropdown.Menu aria-label="More for the plan" onAction={(key) => (key === 'redeem' ? onRedeem() : onManage())}>
              {b.canRedeem ? (
                <Dropdown.Item id="redeem" textValue="Redeem a licence">
                  <Label>Redeem a licence</Label>
                </Dropdown.Item>
              ) : null}
              {b.hasBillingAccount ? (
                <Dropdown.Item id="manage" textValue="Manage billing">
                  <Label>Manage billing</Label>
                </Dropdown.Item>
              ) : null}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </>
    );
  }
  return (
    <ListGroup title="Plan" description={b.configured ? (licensed(b) ? 'Licensed by Mojo Up AI Cloud.' : `${b.priceText}.`) : undefined} actions={actions}>
      <ListRow icon={<CreditCard />} title={planName(b)} description={standing(b)} {...(b.status === 'none' ? {} : { status: <StatusMark state={status.state} label={status.label} /> })} />
      {over > 0 ? <ListBlock className="text-xs text-muted">This team has {plural(over, 'more person', 'more people')} than its plan allows. Everyone keeps their access; nobody new can join until there is a seat.</ListBlock> : null}
      {b.stale ? <ListBlock className="text-xs text-muted">Billing has not answered{b.refreshedAt ? ` since ${timeAgo(b.refreshedAt)}` : ' lately'}, so AI Cloud is going by its last word.</ListBlock> : null}
      {!owner && b.configured ? <ListBlock className="text-xs text-muted">The team's owners change the plan and its seats.</ListBlock> : null}
    </ListGroup>
  );
}

// ── Usage ─────────────────────────────────────────────────────────────────────

/** How full a quota is: room, nearly full (80% or more), full, or not part of the plan at all. */
function fillOf(r: CloudQuotaRow): { fill: Fill; ratio: number } {
  const n = r.used ?? r.thisMinute;
  if (r.limit === null) return { fill: 'unlimited', ratio: 0 };
  if (r.limit === 0) return { fill: 'none', ratio: 0 };
  const ratio = (n ?? 0) / r.limit;
  return { fill: r.atLimit || ratio >= 1 ? 'full' : ratio >= 0.8 ? 'near' : 'room', ratio };
}

/** "3 of 10", "12 MB of 20 MB", "120 this minute, of 2,000 a minute", "4, no limit". */
function amountText(r: CloudQuotaRow, plan: string): string {
  const amount = (n: number) => quotaAmountText(r.unit === 'perMinute' ? 'count' : r.unit, n);
  if (r.unit === 'perMinute') {
    const limit = r.limit === null ? 'no limit' : quotaAmountText('perMinute', r.limit);
    return r.thisMinute === undefined ? `Up to ${limit}` : `${amount(r.thisMinute)} this minute, of ${limit}`;
  }
  if (r.limit === 0) return `Not part of ${plan}`;
  if (r.limit === null) return r.used === undefined ? 'No limit' : `${amount(r.used)}, no limit`;
  return r.used === undefined ? `Up to ${amount(r.limit)}` : `${amount(r.used)} of ${amount(r.limit)}`;
}

/** The members row reads as seats: people and open invitations hold one each. */
const rowLabel = (r: CloudQuotaRow) => (r.key === 'members' ? 'Members and invitations' : r.key === 'ingestRecordsPerMinute' ? 'Records from machines a minute' : r.key === 'requestsPerMinute' ? 'Requests a minute' : r.label);

function Meter({ row, plan }: { row: CloudQuotaRow; plan: string }) {
  const { fill, ratio } = fillOf(row);
  return (
    <li className="flex items-start gap-3">
      <Ring fill={fill} ratio={ratio} />
      <div className="grid min-w-0 flex-1 gap-0.5">
        <span className="text-sm font-medium">{rowLabel(row)}</span>
        <span className="text-xs tabular-nums text-muted">{amountText(row, plan)}</span>
        {fill === 'full' ? <StatusMark state="error" label="At the limit" /> : fill === 'near' ? <StatusMark state="medium" label="Nearly full" /> : null}
        {row.atLimit ? <p className="text-xs text-muted">{row.atLimit}</p> : null}
      </div>
    </li>
  );
}

function UsageTab({ quotas: q }: { quotas?: CloudOrganisationQuotas }) {
  if (!q) return null;
  const mine = q.rows.filter((r) => r.scope === 'person');
  // The rates (requests and records a minute) are abuse protection, not something the plan advertises.
  const ours = q.rows.filter((r) => r.scope === 'organisation' && r.unit !== 'perMinute');
  // The meters stand in a grid inside one card: columns by the card's own width, not the window's.
  const grid = 'm-0 grid list-none gap-x-6 gap-y-5 p-0 @[34rem]:grid-cols-2 @[52rem]:grid-cols-3';
  return (
    <>
      <ListGroup title="Usage" description={`What ${q.planLabel} allows this organisation, and how much of it is used.`}>
        <ListBlock>
          <ul className={grid}>
            {ours.map((r) => (
              <Meter key={r.key} row={r} plan={q.planLabel} />
            ))}
          </ul>
        </ListBlock>
      </ListGroup>
      {mine.length ? (
        <ListGroup title="Your own allowance" description="Counted for you, across the organisations you are in.">
          <ListBlock>
            <ul className={grid}>
              {mine.map((r) => (
                <Meter key={r.key} row={r} plan={q.planLabel} />
              ))}
            </ul>
          </ListBlock>
        </ListGroup>
      ) : null}
    </>
  );
}

// ── Invoices ──────────────────────────────────────────────────────────────────

function InvoicesTab({ billing: b, details, owner, busy, onOpen, onTrial }: { billing: CloudBilling; details?: CloudBillingDetails; owner: boolean; busy: boolean; onOpen: () => void; onTrial: () => void }) {
  let body: ReactNode;
  if (!b.configured) {
    body = (
      <ArtEmpty tone={tone} icon={<Receipt />} title="Billing is not set up here" compact>
        Teams cannot be bought online here, so there are no invoices.
      </ArtEmpty>
    );
  } else if (!b.hasBillingAccount && licensed(b)) {
    body = (
      <ArtEmpty tone={tone} icon={<Receipt />} title="No invoices: the team runs on a licence" compact>
        This team's licence was issued outside Stripe, so there are no invoices here. Billing questions go to support.
      </ArtEmpty>
    );
  } else if (!b.hasBillingAccount) {
    body = (
      <ArtEmpty
        tone={tone}
        icon={<Receipt />}
        title="No invoices yet"
        action={
          owner ? (
            <Button size="sm" variant="primary" onPress={onTrial}>
              {b.canStartTrial ? 'Start free trial' : 'Switch to Teams'}
            </Button>
          ) : undefined
        }
      >
        Mojo Up Pty Ltd issues a tax invoice, with GST and your ABN, for each Teams payment. The first comes when a trial ends, or at once when the team moves to Teams without one.
      </ArtEmpty>
    );
  } else {
    const to = details?.billingContact?.email;
    body = (
      <ArtEmpty
        tone={tone}
        icon={<Receipt />}
        title="Tax invoices for every payment"
        action={
          owner ? (
            <Button size="sm" variant="primary" onPress={onOpen} isDisabled={busy}>
              Open invoices
            </Button>
          ) : undefined
        }
      >
        Every Teams payment has a tax invoice, with GST and your ABN, emailed to {to ?? 'the billing contact'}. Stripe keeps them: open them there to read or download any of them.{owner ? '' : ' Ask one of the team’s owners to open them.'}
      </ArtEmpty>
    );
  }
  return <ListGroup title="Invoices" description="Tax invoices for the team's Teams payments." empty={body} />;
}

// ── Billing details ───────────────────────────────────────────────────────────

/** One fact: its label as the row's title, the value at the end, and a note under the label when there is one. */
function Row({ label, note, children }: { label: string; note?: ReactNode; children: ReactNode }) {
  return <ListRow title={label} description={note} value={<span className="break-words text-foreground">{children}</span>} />;
}

const personText = (p: { name?: string; email?: string }) => (p.name && p.email ? `${p.name} (${p.email})` : (p.name ?? p.email ?? 'Someone'));

function DetailsTab({ billing: b, details: d, owner, busy, onManage }: { billing: CloudBilling; details?: CloudBillingDetails; owner: boolean; busy: boolean; onManage: () => void }) {
  const used = b.members + b.openInvitations;
  const next = nextDate(b);
  const renewal =
    b.status === 'trialing' && b.trialEnd
      ? `The trial ends on ${longDay(b.trialEnd)}; the first payment follows.`
      : LIVE.has(b.status) && b.periodEnd
        ? b.cancelAtPeriodEnd
          ? `None: Teams ends on ${longDay(b.periodEnd)}.`
          : longDay(b.periodEnd)
        : (next?.detail ?? '—');
  return (
    <ListGroup
      title="Billing details"
      description="Who the team is billed to, its owners, its plan and its seats."
      actions={
        owner && b.configured && b.hasBillingAccount ? (
          <Button size="sm" variant="secondary" onPress={onManage} isDisabled={busy}>
            Manage billing
          </Button>
        ) : undefined
      }
    >
      <Row label="Team">{d?.organisationName ?? '—'}</Row>
      <Row label="Billing contact" note={d?.billingContact ? 'Receipts and tax invoices go to this address. The address, the card, the legal name and the ABN change in Manage billing.' : 'Not set yet. It is whoever first starts Teams, with the address they sign in with.'}>
        {d?.billingContact ? personText(d.billingContact) : '—'}
      </Row>
      <Row label={d && d.owners.length === 1 ? 'Owner' : 'Owners'}>{d ? d.owners.map(personText).join(', ') || '—' : '—'}</Row>
      <Row label="Plan">{`${b.plan === 'teams' ? 'Teams' : 'Free'}${d ? `, since ${longDay(d.planSince ?? d.createdAt)}` : ''}`}</Row>
      {d?.billingContact ? <Row label="Subscription started">{longDay(d.billingContact.since)}</Row> : null}
      {b.licence ? (
        <Row label="Licence" note={`${b.licence.note ? `${b.licence.note} ` : ''}Redeemed with a code. Ask support to change or extend it.`}>
          {plural(b.licence.seats, 'seat')} until {longDay(b.licence.until)}
          {b.licence.ended ? ', ended' : ''}
        </Row>
      ) : null}
      <Row label={licensed(b) ? 'Licence ends' : 'Next renewal'}>{licensed(b) && b.licence ? longDay(b.licence.until) : renewal}</Row>
      <Row label="Seats" note={`${plural(b.members, 'person', 'people')}${b.openInvitations ? ` and ${plural(b.openInvitations, 'open invitation')}` : ''}; each holds a seat.`}>
        <span className="tabular-nums">{b.plan === 'teams' ? `${plural(b.seatsPurchased, 'seat')} bought, ${used} used` : `${plural(b.seatLimit, 'seat')} on the free plan, ${used} used`}</span>
      </Row>
      {b.configured && !licensed(b) ? <Row label="Price">{b.priceText}</Row> : null}
      {b.configured ? <Row label="Last word from billing">{b.refreshedAt ? timeAgo(b.refreshedAt) : 'Not yet: the team has never subscribed'}</Row> : null}
    </ListGroup>
  );
}

// ── Add-ons ───────────────────────────────────────────────────────────────────

function AddOnsTab({ organisation, billing: b, owner, onSeats, onTrial }: { organisation: string; billing: CloudBilling; owner: boolean; onSeats: () => void; onTrial: () => void }) {
  const live = LIVE.has(b.status);
  const seatsAction =
    owner && b.configured && licensed(b) ? (
      <span className="text-sm text-muted">In the licence: ask support</span>
    ) : owner && b.configured ? (
      live ? (
        <Button size="sm" variant="secondary" onPress={onSeats}>
          Manage seats
        </Button>
      ) : NEEDS_CARD.has(b.status) ? (
        <span className="text-sm text-muted">After the payment is sorted</span>
      ) : (
        <Button size="sm" variant="secondary" onPress={onTrial}>
          {b.canStartTrial ? 'Start free trial' : 'Switch to Teams'}
        </Button>
      )
    ) : (
      <span className="text-sm text-muted">{b.configured ? 'Owners add them' : 'Not sold here'}</span>
    );
  const items: Array<{ title: string; text: string; trailing: ReactNode }> = [
    { title: 'Extra seats', text: licensed(b) ? 'One for each person or open invitation. The licence sets how many; support changes it.' : `One for each person or open invitation, at ${b.priceText}. Seats added are charged now for the rest of the month; fewer are billed from the next renewal.`, trailing: seatsAction },
    { title: 'More storage', text: "Room past the plan's allowance for design-system files, backups and memory.", trailing: <span className="text-sm text-muted">On request</span> },
    { title: 'Larger allowances', text: 'More projects, catalogue entries or design systems than the plan holds.', trailing: <span className="text-sm text-muted">On request</span> },
  ];
  return (
    <ListGroup
      title="Add-ons"
      description="Extra seats are bought here; ask us for the rest."
      actions={
        <Button size="sm" variant="secondary" onPress={() => contactSales(organisation)}>
          Contact sales
        </Button>
      }
    >
      {items.map((i) => (
        <ListRow key={i.title} icon={<CirclePlus />} title={i.title} description={i.text} control={i.trailing} />
      ))}
    </ListGroup>
  );
}

// ── Enterprise ────────────────────────────────────────────────────────────────

/** A self-hosted AIOE is licensed, not billed here: the portal sells the licence. */
function EnterpriseBilling() {
  const navigate = useNavigate();
  // Administrators read the licence; anyone else is told where it is.
  const licence = useLoad(() => routes.licence().catch(() => undefined as LicenceView | undefined), []);
  const v = licence.data;
  return (
    <Page>
      <PageHeader tone={tone} icon={<CreditCard />} title="Billing" subtitle="This AIOE is licensed through the Mojo Up customer portal, where the organisation's subscription, seats and invoices are managed." />
      <PartBoundary key="licence" name="Billing" tone={tone}>
      <div className="art-stats">
        <Stat tone={tone} icon={<CreditCard />} label="Licence" value={v ? (v.plan === 'enterprise' ? 'Enterprise' : v.plan === 'teams' ? 'Teams' : 'Free') : undefined} detail={v?.holder ?? (v ? 'No holder named' : 'Administrators see it')} />
        <Stat tone={tone} icon={<CreditCard />} label="People" value={v ? (v.seats ? `${v.peopleInUse} of ${v.seats}` : v.peopleInUse) : undefined} detail={v ? 'Used a workbench this month' : ''} />
        <Stat tone={tone} icon={<Receipt />} label="Expires" value={v ? (v.expiresAt ? shortDay(v.expiresAt) : v.type === 'unlimited' ? 'Never' : '—') : undefined} detail={v?.expiresAt ? `${plural(v.graceDays, 'day')} of grace after` : ''} />
      </div>
      <ListGroup
        title="Licence"
        description="The Mojo Up customer portal sells the licence and issues its invoices to whoever holds the organisation's subscription there. It signs a licence token that this AIOE checks."
        actions={
          <>
            <Button size="sm" variant="primary" onPress={() => navigate('/settings/organisation')}>
              Licence details
            </Button>
            <Button size="sm" variant="secondary" onPress={() => navigate('/updates')}>
              Updates
            </Button>
          </>
        }
      >
        <ListRow
          icon={<CreditCard />}
          title="Standing"
          description={v?.banner ? v.banner.text : 'Install or replace the licence token in Settings, Organisation, where its standing is. Which releases it covers is on Updates.'}
          {...(v ? { status: licenceMark(v) } : {})}
        />
      </ListGroup>
      <ListGroup title="Costs inside the organisation">
        <ListRow to="/costs" icon={<Receipt />} title="Costs and Budgets" description="What the work cost, by area, project, team and person, and the chargeback statements for them. Those are this organisation's own figures, not Mojo Up's invoices." />
      </ListGroup>
      </PartBoundary>
    </Page>
  );
}
